import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box } from '@chakra-ui/react'

import { useToast } from '@chakra-ui/react'

import {
    Attachment,
    MessageType,
    MessageMethod,
    MessageOrigin,
    AttachmentType,
    ChatFieldsFragment,
    ChatMessageFieldsFragment,
    CreateAttachmentInput,
    CreateMessageMutation,
    GetChatMessagesByChatIdQueryVariables,
    useCreateMessageMutation,
    useCreateScheduledMessageMutation,
    useSuggestChatResponseLazyQuery,
    QueryScheduledMessagesDocument,
    ChatStatus,
    ClientPhoneFieldsFragment,
} from '~graphql/generated/graphql'
import { useChat } from '../chat-context'
import { useUserContext } from '~config/user-context'
import { SetStateFn } from '~config/constants'
import { useChatPresence } from '../presence/use-chat-presence'
import ChatPresenceIndicator from '../presence/chat-presence-indicator'
import { uploadMessageAttachment } from '~utils/storage-helpers'
import {
    useToastFeedback,
    useGenericToasts,
} from '~utils/hooks/use-toast-feedback'
import { getEmailedDisabledMessage, getSmsDisabledMessage } from './helpers'
import { useStateWithReset } from '~utils/hooks/use-state-with-reset'
import {
    getTemplateMapper,
    replaceTemplateVariables,
} from '~utils/replace-template-variables'
import { useLocalStorage } from '~utils/hooks/use-local-storage'
import { LocalStorageKeys } from '~config/constants'
import {
    clearChatDraft,
    useChatDraft,
    writeChatDraft,
} from '~utils/hooks/use-chat-draft'
import ChatInputComponent from './input-component'
import ScheduledMessagesBanner from './scheduled-messages-banner'
import { PatientIdAndName } from './templates/types'
import {
    buildOptimisticChatMessage,
    insertMessageIntoChatQuery,
    removeOptimisticMessagesFromChatQuery,
} from '../insert-chat-message'
import { isChatSendInFlight, trackChatSend } from './send-message-lock'
import { useApolloClient } from '@apollo/client'

interface Props {
    refetch: () => void
    messagesQueryVariables: GetChatMessagesByChatIdQueryVariables
    useMessageListCache: boolean
    /**
     * Compact layout for embedded contexts like the Whiteboard chat popover.
     * Hides secondary action buttons (attachments, templates, links, AI suggestions).
     */
    compact?: boolean
    /**
     * Pre-fills the draft with this text on first mount only if the user has
     * no existing draft for this chat. Used by OpenChatButton's `defaultText`.
     */
    initialDraft?: string
    /**
     * If provided on first mount, fires `suggestChatResponse` with this prompt
     * and seeds the input with the AI-generated reply (only when no existing
     * draft is present). Used by OpenChatButton's `aiPrompt`.
     */
    aiPrompt?: string
}

function getCurrentClientPhoneForSelection({
    clientPhone,
    clientPhones,
}: {
    clientPhone: ClientPhoneFieldsFragment | undefined
    clientPhones: ClientPhoneFieldsFragment[] | null | undefined
}): ClientPhoneFieldsFragment | undefined {
    if (!clientPhone) return undefined

    const currentClientPhones = clientPhones ?? []

    return (
        currentClientPhones.find(cp => cp.id === clientPhone.id) ??
        currentClientPhones.find(cp => cp.phone === clientPhone.phone)
    )
}

function getSinglePatientId(patientIds: string[]): string | undefined {
    return patientIds.length === 1 ? patientIds[0] : undefined
}

function toChatMessageFields(
    message: CreateMessageMutation['createMessage'],
    chat: ChatFieldsFragment,
): ChatMessageFieldsFragment {
    return {
        ...message,
        chat: {
            __typename: 'Chat',
            id: chat.id,
            clinic: {
                __typename: 'Clinic',
                id: chat.clinic.id,
                name: chat.clinic.name ?? null,
            },
        },
        patients: message.patients ?? [],
    }
}

export default function ChatViewInput({
    refetch,
    messagesQueryVariables,
    useMessageListCache,
    compact = false,
    initialDraft,
    aiPrompt,
}: Props) {
    const { user } = useUserContext()
    const {
        chat,
        clientPhone,
        resetClientPhone,
        patientFilter,
        debouncedPatientFilter,
        availablePhones,
        setClientPhone,
        baseClientPhone,
    } = useChat()
    const isChatClosed = chat.status === ChatStatus.Closed

    const [text, setText] = useChatDraft(chat.id, user?.id, initialDraft)

    const {
        typingMembers,
        viewingMembers,
        reportTyping,
        reportStoppedTyping,
    } = useChatPresence(chat.id)

    // Every composer change marks us as typing for other viewers; the
    // presence hook flips us back to viewing after 3s of silence.
    const handleTextChange: SetStateFn<string> = useCallback(
        value => {
            reportTyping()
            setText(value)
        },
        [reportTyping, setText],
    )
    const [messageTemplatePatient, setMessageTemplatePatient] = useState<
        PatientIdAndName | undefined
    >(undefined)

    /**
     * Fire suggestChatResponse exactly once per chat opening when an aiPrompt
     * is provided. Skipped when the user already has a draft -- we don't want
     * to overwrite their in-progress message.
     */
    const [suggestChatResponse] = useSuggestChatResponseLazyQuery()
    const hasFiredAiPrompt = useRef(false)
    useEffect(() => {
        if (
            !aiPrompt ||
            hasFiredAiPrompt.current ||
            !chat?.id ||
            text.length > 0
        ) {
            return
        }
        hasFiredAiPrompt.current = true
        suggestChatResponse({
            variables: { chatId: chat.id, existingText: aiPrompt },
        }).then(({ data }) => {
            const response = data?.suggestChatResponse?.response
            if (response) setText(response)
        })
    }, [aiPrompt, chat?.id, text, suggestChatResponse, setText])

    const smsDisabledMessage = getSmsDisabledMessage(chat.client)
    const emailDisabledMessage = getEmailedDisabledMessage(chat.client)
    const { onError } = useToastFeedback(
        '',
        'Please ensure that your clinic email and phone are verified with Chckvet.',
    )
    const { onError: onGenericError } = useGenericToasts(
        undefined,
        'An error occurred.',
    )

    const [loading, setLoading] = useState(false)
    const [isInternal, setIsInternal] = useState(false)
    const [attachments, setAttachments] = useState<Attachment[]>([])
    const [messageMethods, setMessageMethods, resetMessageMethods] =
        useStateWithReset<MessageMethod[]>(
            !smsDisabledMessage ? [MessageMethod.Sms] : [],
        )

    const [shouldCloseChatOnSend, setShouldCloseChatOnSend] = useLocalStorage(
        LocalStorageKeys.closeChatOnSend,
        false,
    )
    const [shouldOpenChatOnSend, setShouldOpenChatOnSend] = useLocalStorage(
        LocalStorageKeys.openChatOnSend,
        false,
    )

    const toast = useToast()
    const apolloClient = useApolloClient()

    const [createMessage] = useCreateMessageMutation()

    const [createScheduledMessage, { loading: isScheduling }] =
        useCreateScheduledMessageMutation({
            refetchQueries: [QueryScheduledMessagesDocument],
        })

    const convertToAttachmentInput = async (
        attachment: Attachment,
        chatId: string,
    ): Promise<CreateAttachmentInput | undefined> => {
        // Check if the URL is already a Firebase Storage URL
        if (
            attachment.url.startsWith('https://firebasestorage.googleapis.com')
        ) {
            // If it's already uploaded (likely a video), return the existing info
            return {
                url: attachment.url,
                filename: attachment.filename, // Use the original filename
                type: attachment.type, // Use the original type (should be Video)
            }
        } else {
            // If it's a data URL (image/pdf), upload it
            const path = `chats/${chatId}/attachments`
            const attachmentData = await uploadMessageAttachment(
                path,
                attachment.filename,
                attachment.url,
            )

            if (!attachmentData) return undefined

            // Determine type from metadata contentType
            let type = AttachmentType.Image // Default to Image
            const contentType =
                attachmentData.metadata.contentType?.toLowerCase() ?? ''

            if (contentType.includes('pdf')) {
                type = AttachmentType.Pdf
            } else if (contentType.includes('video')) {
                // This case might not be hit if videos are pre-uploaded,
                // but good to have for future changes.
                type = AttachmentType.Video
            }

            return {
                url: attachmentData.downloadUrl,
                // Keep the original display name; storage object name is uuid-prefixed.
                filename: attachment.filename,
                type: type,
            }
        }
    }

    function handleResetMessageMethods() {
        resetMessageMethods()
        resetClientPhone()
        setIsInternal(false)
    }

    async function handleSendMessage() {
        if (!text && attachments.length === 0) return
        if (isChatSendInFlight(chat.id)) return

        const textToSend = text
        const attachmentsToSend = attachments
        const templatePatientToSend = messageTemplatePatient
        const methodsToSend =
            messageMethods.length === 0
                ? [MessageMethod.Push]
                : messageMethods

        const currentClientPhone = getCurrentClientPhoneForSelection({
            clientPhone,
            clientPhones: chat.client.client_phones,
        })
        const isSelectedBaseClientPhone =
            !!clientPhone?.phone && clientPhone.phone === chat.client.phone

        if (clientPhone && !currentClientPhone && !isSelectedBaseClientPhone) {
            onGenericError()
            return
        }

        const filteredPatientId = getSinglePatientId(debouncedPatientFilter)
        const selectedPatient =
            templatePatientToSend ??
            chat.client.patients?.find(p => p.id === filteredPatientId)

        const { value: replacedText } = replaceTemplateVariables(
            textToSend,
            getTemplateMapper({
                client: chat.client,
                clinic: chat.clinic,
                patient: selectedPatient,
            }),
        )

        clearChatDraft(chat.id, user?.id)
        setText('')
        reportStoppedTyping()
        setMessageTemplatePatient(undefined)
        setAttachments([])
        setLoading(true)

        const sendPromise = (async () => {
            let optimisticInserted = false
            try {
                if (useMessageListCache) {
                    insertMessageIntoChatQuery(
                        apolloClient.cache,
                        messagesQueryVariables,
                        buildOptimisticChatMessage({
                            chat,
                            text: replacedText,
                            attachments: attachmentsToSend,
                            messageMethods: methodsToSend,
                            patientId: selectedPatient?.id,
                            patientName: selectedPatient?.name,
                            clinicUser: user,
                            clientPhone: currentClientPhone,
                            hiddenForClient: isInternal,
                        }),
                    )
                    optimisticInserted = true
                }

                let attachmentInputs: CreateAttachmentInput[] | undefined =
                    undefined

                if (attachmentsToSend.length > 0) {
                    const processedAttachments = await Promise.all(
                        attachmentsToSend.map(a =>
                            convertToAttachmentInput(a, chat.id),
                        ),
                    )
                    attachmentInputs = processedAttachments.filter(
                        Boolean,
                    ) as CreateAttachmentInput[]

                    const failedCount =
                        attachmentsToSend.length - attachmentInputs.length
                    if (failedCount > 0) {
                        console.warn(
                            `${failedCount} attachment(s) failed to process during send.`,
                        )
                        onGenericError(
                            `${failedCount} attachment(s) could not be prepared and were not sent. Please try removing and re-adding them.`,
                        )
                    }
                }

                const result = await createMessage({
                    variables: {
                        chatId: chat.id,
                        data: {
                            text: replacedText,
                            attachments: attachmentInputs,
                            message_type: MessageType.User,
                            message_methods: methodsToSend,
                            patient: selectedPatient?.id,
                            message_origin: MessageOrigin.ChckupDashboard,
                            clinic_user: user?.id,
                            client_phone: currentClientPhone?.id,
                            hidden_for_client: isInternal,
                            should_change_chat_status_to: isChatClosed
                                ? shouldOpenChatOnSend
                                    ? ChatStatus.Open
                                    : undefined
                                : shouldCloseChatOnSend
                                  ? ChatStatus.Closed
                                  : undefined,
                        },
                    },
                })

                const createdMessage = result.data?.createMessage
                if (useMessageListCache && createdMessage) {
                    insertMessageIntoChatQuery(
                        apolloClient.cache,
                        messagesQueryVariables,
                        toChatMessageFields(createdMessage, chat),
                        true,
                    )
                } else {
                    refetch()
                }
            } catch (error) {
                if (optimisticInserted) {
                    removeOptimisticMessagesFromChatQuery(
                        apolloClient.cache,
                        messagesQueryVariables,
                    )
                }
                writeChatDraft(chat.id, user?.id, textToSend)
                setText(textToSend)
                setMessageTemplatePatient(templatePatientToSend)
                setAttachments(attachmentsToSend)
                onError()
                console.error({ error })
            } finally {
                setLoading(false)
            }
        })()

        await trackChatSend(chat.id, sendPromise)
    }

    async function handleScheduleSend(sendTime: number) {
        if (!text && attachments.length === 0) return
        if (!user?.id || !chat.clinic?.id) return

        try {
            let attachmentInputs: CreateAttachmentInput[] | undefined =
                undefined

            if (attachments.length > 0) {
                const processedAttachments = await Promise.all(
                    attachments.map(a =>
                        convertToAttachmentInput(a, chat.id),
                    ),
                )
                attachmentInputs = processedAttachments.filter(
                    Boolean,
                ) as CreateAttachmentInput[]
            }

            const filteredPatientId = getSinglePatientId(debouncedPatientFilter)
            const selectedPatient =
                messageTemplatePatient ??
                chat.client.patients?.find(p => p.id === filteredPatientId)

            const { value: replacedText } = replaceTemplateVariables(
                text,
                getTemplateMapper({
                    client: chat.client,
                    clinic: chat.clinic,
                    patient: selectedPatient,
                }),
            )

            await createScheduledMessage({
                variables: {
                    data: {
                        clinic: chat.clinic.id,
                        created_by: user.id,
                        chat: chat.id,
                        client: chat.client.id,
                        text: replacedText,
                        message_methods:
                            messageMethods.length === 0
                                ? [MessageMethod.Push]
                                : messageMethods,
                        send_time: sendTime,
                        message_attachments: attachmentInputs,
                    },
                },
            })

            setText('')
            reportStoppedTyping()
            setMessageTemplatePatient(undefined)
            setAttachments([])
            toast({
                status: 'success',
                description: 'Message scheduled successfully.',
                duration: 3000,
                position: 'top',
            })
        } catch (error) {
            console.error({ error })
            toast({
                status: 'error',
                description: 'Failed to schedule message. Please try again.',
                duration: 5000,
                position: 'top',
            })
        }
    }

    return (
        <Box w="full" px={4} pb={4}>
            {chat.clinic?.id && (
                <ScheduledMessagesBanner
                    chatId={chat.id}
                    clinicId={chat.clinic.id}
                />
            )}
            <ChatPresenceIndicator
                typingMembers={typingMembers}
                viewingMembers={viewingMembers}
            />
            <ChatInputComponent
                chat={chat}
                text={text}
                setText={handleTextChange}
                loading={loading}
                handleSendMessage={handleSendMessage}
                isInternal={isInternal}
                setIsInternal={setIsInternal}
                smsDisabledMessage={smsDisabledMessage}
                emailDisabledMessage={emailDisabledMessage}
                messageMethods={messageMethods}
                setMessageMethods={setMessageMethods}
                availablePhones={availablePhones}
                clientPhone={clientPhone}
                setClientPhone={setClientPhone}
                baseClientPhone={baseClientPhone}
                resetMessageMethods={resetMessageMethods}
                attachments={attachments}
                setAttachments={setAttachments}
                shouldCloseChatOnSend={shouldCloseChatOnSend}
                setShouldCloseChatOnSend={setShouldCloseChatOnSend}
                shouldOpenChatOnSend={shouldOpenChatOnSend}
                setShouldOpenChatOnSend={setShouldOpenChatOnSend}
                onScheduleSend={isInternal ? undefined : handleScheduleSend}
                isScheduling={isScheduling}
                patientFilter={patientFilter}
                onCopyTemplatePatient={setMessageTemplatePatient}
                compact={compact}
            />
        </Box>
    )
}
